import * as React from 'react';
import { Flex, Link, useTheme } from '@aws-amplify/ui-react';
import { PrismicLink } from '@prismicio/react';
import HtmlText from '@components/text/htmlText';
import Container from '@components/container';
import PrimaryButton from '@components/buttons/primary';
import styled, { css } from 'styled-components';
import { media } from '@theme/mediaQueries';
import VideoArea from './videoArea';
import ImageCredits from '@components/image-credits/imageCredits';

interface Link {
  text: string;
  link: any;
}

interface HeroProps {
  title: any;
  text?: any;
  backgroundImage?: any;
  backgroundVideo?: any;
  videoCopyright?: any;
  page?: any;
  button?: Link;
  extraLink?: Link;
}

interface ImageProps {
  $backgroundImage?: any;
  $backgroundVideo?: any;
  page?: any;
}

interface InnerContentProps {
  $page?: any;
}

const ImageStyles = styled.div<ImageProps>`
  position: relative;
  width: 100%;
  min-height: 285px;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  background-image: ${(props) =>
    props?.$backgroundVideo?.embed_url?.length != undefined
      ? ''
      : `url(${props?.$backgroundImage.url})`};

  ${({ backgroundVideo }: any) =>
    backgroundVideo &&
    css`
      overflow: hidden;
      background-color: ${(props) => props?.theme.tokens.colors.primary.value};
      display: block;

      .wwi-hero-video {
        position: absolute;
        top: 0;
        left: 0;
      }
    `}

  ${({ page }: any) =>
    page == 'article' &&
    css`
      ${media('medium')`
        align-items: flex-end;
      `};
    `}

  ${({ page }: any) =>
    page == 'homepage' &&
    css`
      min-height: 550px;
    `}

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    z-index: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      ${(props) => props?.theme.tokens.colors.primary.value} -10.66%,
      rgba(35, 35, 35, 0) 180%
    );
  }

  ${(props) =>
    media('medium')`
    &::after {
      background: linear-gradient(
        90deg,
        ${props?.theme.tokens.colors.primary.value} -10.66%,
        rgba(35, 35, 35, 0) 75.46%
      );
    }
  `}
`;

const WrapperStyles = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  z-index: 1;
  width: 100%;
`;

const ContentStyles = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 24px 0;
  height: 100%;
  z-index: 2;
  color: ${(props) => props?.theme.tokens.colors.white.value};
  width: 100%;
  text-align: center;

  .amplify-heading {
    color: ${(props) => props?.theme.tokens.colors.white.value};
  }

  ${media('medium')`
    text-align: left;
  `};
`;

const InnerContentStyles = styled.div<InnerContentProps>`
  ${({ $page }: any) =>
    $page == 'homepage' &&
    css`
      max-width: 488px;

      .amplify-heading {
        margin-bottom: 37px;
      }

      ${media('medium')`
        .amplify-heading {
          margin-bottom: 29px;
        }
      `};
    `}

  ${({ page }: any) =>
    page == 'article' &&
    css`
      max-width: 622px;

      .amplify-heading {
        margin-bottom: 25px;
      }

      ${media('medium')`
        .amplify-heading {
          margin-bottom: 15px;
        }

        p {
          max-width: 437px;
        }
      `};
    `}

  ${({ page }: any) =>
    page != 'homepage' &&
    css`
      .amplify-heading {
        font-size: var(--font-size-h2);
      }
    `}

  p {
    font-size: 1.125rem;
    margin-top: 0;
  }

  ${media('medium')`
    p {
      font-size: 1.375rem;
    }
  `};
`;

const HeroIconStyles = styled.div`
  &::after {
    content: '';
    position: absolute;
    z-index: 1;
    left: -175px;
    top: 85%;
    width: 234px;
    height: 105px;
    background-image: url('/../../images/bolt--hero.svg');
    background-size: contain;
    background-repeat: no-repeat;
  }

  ${media('medium')`
    &::after {
      left: -135px;
    }
  `};

  ${media('xl')`
    &::after {
      left: -95px;
    }
  `};
`;

const Hero = ({
  title,
  text,
  button,
  extraLink,
  backgroundImage,
  backgroundVideo,
  videoCopyright,
  page,
  ...props
}: HeroProps) => {
  const theme = useTheme();

  return (
    <section className="wwi-relative">
      <ImageStyles
        theme={theme}
        $backgroundImage={backgroundImage}
        $backgroundVideo={backgroundVideo}
        page={page}
        {...props}
      >
        {backgroundImage.copyright && !backgroundVideo?.embed_url ? (
          <ImageCredits
            copyright={backgroundImage.copyright}
            position="overlay-top"
          />
        ) : (
          ''
        )}
        {backgroundVideo?.embed_url ? (
          <VideoArea
            page={page}
            backgroundVideo={backgroundVideo}
            videoCopyright={videoCopyright}
          />
        ) : (
          ''
        )}
        <WrapperStyles>
          <ContentStyles theme={theme}>
            <Container>
              <InnerContentStyles $page={page} {...props}>
                <Flex
                  direction="column"
                  alignItems={{ base: 'center', medium: 'flex-start' }}
                  gap="0"
                >
                  {title && <HtmlText field={title} />}
                  {text && <HtmlText field={text} />}
                  {button?.link && (
                    <>
                      <PrimaryButton hasIcon="true" field={button.link}>
                        {button.text}
                      </PrimaryButton>
                      <br />
                    </>
                  )}
                  {extraLink?.link && (
                    <PrismicLink field={extraLink.link} className="text-link">
                      {extraLink.text}
                    </PrismicLink>
                  )}
                </Flex>
              </InnerContentStyles>
            </Container>
          </ContentStyles>
          <HeroIconStyles></HeroIconStyles>
        </WrapperStyles>
      </ImageStyles>
    </section>
  );
};

export default Hero;
